import { DateTime } from "luxon";
import { TimeString } from "../config/config";

// Global window extensions for time debugging
declare global {
    interface Window {
        setNow?: string;
        addNow?: string;
        timeShift?: number;
        getNowMaybeSimulated?: () => number;
    }
}

/**
 * Gets the current timestamp, potentially shifted for debugging purposes.
 * Use this instead of Date.now() throughout the app.
 * 
 * Time simulation via browser console:
 * - `window.setNow = "20:11"` - set time to 20:11 (today)
 * - `window.addNow = "05:30"` - add 5 minutes 30 seconds to current time
 * - `window.setNow = "reset"` - reset to real time
 */
export const getNowMaybeSimulated = (): number => {
    const realNow = Date.now();
    if (typeof window !== 'undefined' && window.timeShift) {
        return realNow + window.timeShift;
    }
    return realNow;
};

// Make the function globally available for JS files
if (typeof window !== 'undefined') {
    window.getNowMaybeSimulated = getNowMaybeSimulated;
}

/**
 * Gets the current DateTime, potentially shifted for debugging purposes.
 * Use this instead of DateTime.now() throughout the app.
 */
export const getDateTimeNowMaybeSimulated = (): DateTime => {
    const timestamp = getNowMaybeSimulated();
    return DateTime.fromMillis(timestamp);
}

export const consoleLogWithTime = (...logs: any) => {
    console.log(`[${DateTime.local().toISOTime({includeOffset: false})}] `, ...logs);
}

/**
 * This can parse strings like "HH:MM:SS" or "HH:MM" into seconds.
 * If the string is not parseable, it throws an error.
 * @param timeString 
 * @returns 
 */
export const convertTimeOfDayToSeconds = (timeString: TimeString) => {
    const parts = timeString.split(":");
    if (parts.length < 2 || parts.length > 3) {
        throw new Error(`Invalid time string: ${timeString}`);
    }
    const hours = parseInt(parts[0]);
    const minutes = parseInt(parts[1]);
    const seconds = parts.length === 3 ? parseInt(parts[2]) : 0;
    if (isNaN(hours) || isNaN(minutes) || isNaN(seconds)) {
        throw new Error(`Invalid time string: ${timeString}`);
    }
    return hours * 3600 + minutes * 60 + seconds;
}


/**
 * This can parse strings like "HH:MM:SS" or "MM:SS" into seconds.
 * If the string is not parseable, it throws an error.
 * @param timeString 
 * @returns 
 */
export const convertTimeDurationToSeconds = (timeString?: TimeString) => {
    if (!timeString) return 0;
    const parts = timeString.split(":");
    if (parts.length < 2 || parts.length > 3) {
        throw new Error(`Invalid time string: ${timeString}`);
    }
    const hours = parts.length === 3 ? parseInt(parts[0]) : 0;
    const minutes = parseInt(parts[parts.length - 2]);
    const seconds = parseInt(parts[parts.length - 1]);
    if (isNaN(hours) || isNaN(minutes) || isNaN(seconds)) {
        throw new Error(`Invalid time string: ${timeString}`);
    }
    return hours * 3600 + minutes * 60 + seconds;
}

/**
 * Interprets ``seconds`` as a time of day in the given ``timezone`` and converts it to the "HH:MM" (omitting seconds, as they are not needed)
 * @param seconds 
 * @param timezone 
 * @returns a string created by 
 */
export const convertSecondsToLocal_HH_MM: (seconds: number, tomorrowInTimezone: boolean, timezone: string) => { locallyTomorrow: boolean, localTime: string }
    = (seconds: number, tomorrowInTimezone: boolean, timezone: string) => {
        const datetimeInZone = DateTime.fromObject({ hour: 0, minute: 0, second: 0 }, { zone: timezone }).plus({ seconds: seconds }).plus({ days: tomorrowInTimezone ? 1 : 0 });
        const datetimeLocal = datetimeInZone.toLocal();
        const localTime = datetimeLocal.toLocaleString(DateTime.TIME_SIMPLE);

        //check if it's tomorrow in the local timezone: compare date of local time with current local date
        const locallyTomorrow = datetimeLocal.day !== DateTime.local().day;
        // console.debug("convertSecondsToLocalTime", { seconds, timezone, tomorrowInTimezone, datetimeInZone, datetimeLocal, localTime, locallyTomorrow });
        return { locallyTomorrow, localTime };
    }

/**
 * Interprets ``seconds`` as a time of day and converts it "(H)H:MM:SS"
 * @param seconds 
 * @returns a string created by 
 */
export const convertSecondsTo_H_MM_SS: (seconds: number) => string = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds - hours * 3600) / 60);
    const secondsLeft = seconds - hours * 3600 - minutes * 60;
    const timeString = `${hours}:${minutes < 10 ? "0" : ""}${minutes}:${secondsLeft < 10 ? "0" : ""}${secondsLeft}`;
    // console.debug("secondsTo_H_MM_SS", { seconds, timeString });
    return timeString;
}

/**
 * Displays the duration in seconds as "MM:SS" (or "HH:MM:SS" if it's longer than 1 hour)
 * @param seconds 
 * @returns 
 */
export const convertSecondsToDurationString: (seconds: number) => string
    = (seconds: number) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds - hours * 3600) / 60);
        const secondsLeft = seconds - hours * 3600 - minutes * 60;
        const durationString = `${hours > 0 ? hours + ":" : ""}${minutes < 10 ? "0" : ""}${minutes}:${secondsLeft < 10 ? "0" : ""}${secondsLeft}`;
        // console.debug("secondsToDurationString", { seconds, durationString });
        return durationString;
    }

/**
 * Displays the duration in seconds as "<M> min <S> s" (or "HH:MM:SS" if it's longer than 1 hour)
 * @param seconds 
 * @returns 
 */
export const convertSecondsToDurationString_Text: (seconds: number) => string
= (seconds: number) => {
    if (seconds > 3600) return convertSecondsToDurationString(seconds);

    const minutes = Math.floor((seconds) / 60);
    const leftoverSeconds = seconds - minutes * 60;
    if (minutes > 0 && leftoverSeconds > 0) return `${minutes} min ${seconds - minutes * 60} s`;
    if (minutes > 0) return `${minutes} min`;
    return `${seconds} s`;
}

/**
 * Converts the seconds from the schedule in the timezone to timestamps (i.e. epoch millis as Date.now() would return them, for scheduling via the AudioScheduler).
 * @param seconds seconds since midnight in the configured timezone
 * @param timezone
 * @param shouldBeInTheFuture if true, the seconds are interpreted as "tomorrow" (shifted forward by 24 * 60 * 60) if they are in the past. if false, they are shifted backwards by 24 * 60 * 60 if they are in the future.
 */
export const convertSecondsToTimestamp: (seconds: number, timezone: string, shouldBeInTheFuture?: boolean) => number 
= (seconds: number, timezone: string, shouldBeInTheFuture?: boolean) => {
    const datetime = DateTime.fromObject({ hour: 0, minute: 0, second: 0 }, { zone: timezone }).plus({ seconds: seconds })
    let timestamp = datetime.toMillis();
    const now = getNowMaybeSimulated();
    const isInTheFuture = timestamp > now;
    if (isInTheFuture && !shouldBeInTheFuture) {
        timestamp -= 24 * 60 * 60 * 1000;
    } else if (!isInTheFuture && shouldBeInTheFuture) {
        timestamp += 24 * 60 * 60 * 1000;
    }
    return timestamp;
}